import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';

import { CategoryCard } from 'homepage/components/categoryListSection/CategoryCard';
import { getSearchLandingFeedSubSectionTrackingInfo } from 'homepage/utils/getSearchLandingFeedSubsectionTrackingInfo';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { useIsElementInViewport } from 'shared/hooks/useIsElementInViewport';
import { useStoreDispatch } from 'store/hooks/storeHooks';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { IConstructorUserParams } from 'search/types/ConstructorUserParams';
import { ISubSection } from 'homepage/types/Feed';
import { IModuleTrackingData } from 'homepage/types/ModuleTrackingData';
import { setCurrentFeedData } from 'store/actions/homepageActions';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';

interface ICategoryListSection {
  constructorUserParams?: IConstructorUserParams;
  moduleTrackingData: IModuleTrackingData;
  sectionIndex: number;
  subsections: ISubSection[];
}

export const CategoryListSection = ({
  constructorUserParams,
  moduleTrackingData,
  sectionIndex,
  subsections,
}: ICategoryListSection) => {
  const storeDispatch = useStoreDispatch();
  const moduleRef = useRef();
  const initialInViewport = useIsElementInViewport(moduleRef);

  const [isModuleInViewport, setIsModuleInViewport] = useState(initialInViewport);
  const [currentSubsections, setCurrentSubSections] = useState(subsections);
  const isNewSearchPageEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  const handleSubSectionClick = (index = 0, subsection) => {
    sendTrackingEvent('CMS_MODULE_TAP', {
      ...getSearchLandingFeedSubSectionTrackingInfo(moduleTrackingData, subsection, index),
      service_name: isNewSearchPageEnabled ? 'consumer_search' : 'constructor',
    });
  };

  const handleSubsectionUpdate = (subsectionIndex, constructorResponse) => {
    if (currentSubsections?.length > 0) {
      const newSubsections = [...currentSubsections];
      newSubsections[subsectionIndex] = {
        ...currentSubsections[subsectionIndex],
        constructorResponse,
      };
      setCurrentSubSections(newSubsections);
      storeDispatch(setCurrentFeedData({ index: sectionIndex, subsections: newSubsections }));
    }
  };

  useEffect(() => {
    if (!isModuleInViewport) {
      setIsModuleInViewport(initialInViewport);
    }
  }, [initialInViewport, isModuleInViewport]);

  useEffect(() => {
    storeDispatch(setCurrentFeedData({ index: sectionIndex, subsections: currentSubsections }));
  }, [currentSubsections]);

  return (
    <CategoryListSection.Container ref={moduleRef}>
      {subsections?.map((section, index) => (
        <CategoryCard
          constructorUserParams={constructorUserParams}
          key={section?.uuid || section?.id}
          subsectionIndex={index}
          isModuleInViewport={isModuleInViewport}
          {...section}
          onClick={() => handleSubSectionClick(index, section)}
          handleSubsectionUpdate={handleSubsectionUpdate}
        />
      ))}
    </CategoryListSection.Container>
  );
};

CategoryListSection.Container = styled.div`
  display: flex;
  margin: 32px 0px;
  width: 100vw;
  max-width: 100%;
  padding: 0px 8px;
  ${hideScrollbars}

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    flex-wrap: wrap;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding: 30px 32px;
    width: 100vw;
  }
`;
